/**
 * Pure filter logic for Sales View. Separated from the dashboard so it can be
 * unit-tested without mounting the tree, matching `clientViewSort.ts`.
 */
import type { SalesViewParams } from '../../api/types';

/** Active filter selections. Every value is a list so the entry filters, the chip
 *  rows and the role defaults all speak one shape. */
export interface SalesFilterState {
  cse_manager: string[];
  cse_director: string[];
  cse_territory: string[];
  /** Rep names. What the UI filters on -- see SalesEntryFilters. */
  cse_territory_owner: string[];
  market_center: string[];
  dealer_group: string[];
  tier: string[];
  /** Workflow bands (§7.5) — what to DO, alongside tier's how-bad. */
  band: string[];
  reason_group: string[];
  /** What is going RIGHT, or is not sold yet. Never narrows by health. */
  opportunity: string[];
  engagement_band: string[];
  state: string[];
  city: string[];
  /** Exact client picks from the search dropdown: a client id, a client NAME
   *  (every rooftop called that), or a dealer-group PARENT name. */
  client_token: string[];
  /** CSE-17 leader target list ids. OR within the row, like every other filter. */
  target_list: string[];
}

export interface SalesFlagFilters {
  wentDark: boolean;
  targetMissing: boolean;
}

export const EMPTY_FILTERS: SalesFilterState = {
  cse_manager: [],
  cse_director: [],
  cse_territory: [],
  cse_territory_owner: [],
  market_center: [],
  dealer_group: [],
  tier: [],
  band: [],
  reason_group: [],
  opportunity: [],
  engagement_band: [],
  state: [],
  city: [],
  client_token: [],
  target_list: [],
};

export const NO_FLAGS: SalesFlagFilters = {
  wentDark: false,
  targetMissing: false,
};

/**
 * Server-resolved role defaults -> filter state.
 *
 * Sales View deliberately does NOT port Client View's `computeRoleDefaults`,
 * which parses role-string length client-side against the filter mapping.
 * `/sales-view/filter-options` resolves defaults server-side, so this only has to
 * copy the keys it recognises. An empty `defaults` means admin, or a user with no
 * CSE role, and both are meant to land unfiltered.
 */
/** Keys the filter row can actually CLEAR.
 *
 *  A default on any other key is a filter the rep cannot see and cannot remove. The server
 *  emits `cse_territory` (the territory id) alongside `cse_territory_owner` so an API
 *  consumer filtering on ids has a usable default, but the board has no territory control --
 *  seeding it pinned every rep to their own book, so picking another rep appeared to load
 *  and then showed their own clients back to them.
 */
const CLEARABLE_DEFAULTS: (keyof SalesFilterState)[] = [
  'cse_manager', 'cse_territory_owner', 'market_center', 'client_token',
];

export function applyRoleDefaults(
  defaults: Record<string, string[]> | undefined,
): SalesFilterState {
  const next: SalesFilterState = { ...EMPTY_FILTERS };
  if (!defaults) return next;
  for (const key of CLEARABLE_DEFAULTS) {
    const value = defaults[key];
    if (Array.isArray(value) && value.length > 0) next[key] = [...value];
  }
  return next;
}

/** True when anything is narrowing the board, which is what gates Reset (§6.2). */
export function hasActiveFilters(
  filters: SalesFilterState,
  flags: SalesFlagFilters,
): boolean {
  if (flags.wentDark || flags.targetMissing) return true;
  return Object.values(filters).some((v) => v.length > 0);
}

/**
 * Filter state -> query params.
 *
 * Empty lists are omitted entirely rather than sent as `?tier=`: the backend's
 * `_csv_list` would read a blank string as a real selection matching nothing.
 * The boolean flags are tri-state on the server (true / false / absent), so an
 * unset flag must be absent, NOT `false` -- `went_dark=false` means "only
 * clients that did NOT go dark", which is a different board.
 *
 * `client_token` is the ONE list that stays a list: 96 client names and 94
 * dealer groups in the book contain a comma, so joining them would send
 * "ARS/HEISER FORD" and "INC" as two picks that match nothing.
 *
 * There is no free-text `client_search` here any more. The API still takes it --
 * it is the agent's tool param -- but the board now sends exact picks from the
 * search dropdown, so a rep can SEE what they matched.
 */
export function toParams(
  filters: SalesFilterState,
  flags: SalesFlagFilters,
  impersonateRole?: string | null,
): SalesViewParams {
  const params: SalesViewParams = {};
  for (const [key, value] of Object.entries(filters)) {
    if (key === 'client_token' || value.length === 0) continue;
    params[key as Exclude<keyof SalesFilterState, 'client_token'>] = value.join(',');
  }
  if (filters.client_token.length > 0) params.client_token = [...filters.client_token];
  if (flags.wentDark) params.went_dark = true;
  if (flags.targetMissing) params.target_missing = true;
  if (impersonateRole) params.impersonate_role = impersonateRole;
  return params;
}

/** Toggle one value in a multi-select filter row (OR within a row, §6.2). */
export function toggleValue(current: string[], value: string): string[] {
  return current.includes(value)
    ? current.filter((v) => v !== value)
    : [...current, value];
}

/** Distinct tokens in a CSE-17 paste, split the way the server splits them. */
export function countPasted(text: string): number {
  return new Set(text.split(/[\s,;]+/).map((t) => t.trim()).filter(Boolean)).size;
}
